import { Building2, MapPin, Calendar, FileText, Edit, Trash2, ExternalLink, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const CompanyDetailCard = ({ company, onEdit, onDelete }) => {
  const navigate = useNavigate();

  if (!company) {
    return (
      <div className="company-detail-card bg-card border border-border rounded-lg p-6">
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Select a company to view details</p>
        </div>
      </div>
    );
  }

  const isOverdue = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  return (
    <div className="company-detail-card bg-card border border-border rounded-lg overflow-hidden" data-testid="company-detail-card">
      {/* Header */}
      <div className="bg-muted p-6 border-b border-border">
        <div className="flex items-start justify-between mb-4">
          <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-primary" />
          </div>
          {isOverdue(company.accountsDue) ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
              <AlertCircle className="w-3 h-3" />
              Overdue
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
              <CheckCircle className="w-3 h-3" />
              On Track
            </span>
          )}
        </div>
        <h3 className="text-xl font-semibold mb-1">{company.name}</h3>
        <p className="text-sm text-muted-foreground font-mono">{company.registrationNumber}</p>
      </div>

      {/* Details */}
      <div className="p-6 space-y-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
            <FileText className="w-3 h-3" />
            <span>COMPANY TYPE</span>
          </div>
          <p className="text-sm">{company.companyType}</p>
        </div>

        <div>
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
            <MapPin className="w-3 h-3" />
            <span>ADDRESS</span>
          </div>
          <p className="text-sm">{company.registeredAddress || 'N/A'}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
              <Calendar className="w-3 h-3" />
              <span>YEAR END</span>
            </div>
            <p className="text-sm font-medium">{company.yearEnd || 'N/A'}</p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
              <Calendar className="w-3 h-3" />
              <span>CONF. STATEMENT DUE</span>
            </div>
            <p className="text-sm font-medium">{company.confirmationDue || 'N/A'}</p>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
            <AlertCircle className="w-3 h-3" />
            <span>ACCOUNTS DUE</span>
          </div>
          <p className={`text-sm font-medium ${isOverdue(company.accountsDue) ? 'text-red-600' : ''}`}>
            {company.accountsDue || 'N/A'}
          </p>
        </div>

        {company.sicCodes && (
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
              <FileText className="w-3 h-3" />
              <span>SIC CODES</span>
            </div>
            <p className="text-sm">{Array.isArray(company.sicCodes) ? company.sicCodes.join(', ') : company.sicCodes}</p>
          </div>
        )}

        {company.status && (
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
              <CheckCircle className="w-3 h-3" />
              <span>STATUS</span>
            </div>
            <p className="text-sm font-medium text-green-600">{company.status}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-6 border-t border-border space-y-2">
        <Button
          className="w-full"
          onClick={() => navigate('/wizard/start')}
          data-testid="start-job-detail-btn"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Start New Job
        </Button>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => onEdit && onEdit(company)}
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button 
            variant="outline" 
            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => onDelete && onDelete(company)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailCard;
